import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  Package,
  AlertTriangle,
  ShoppingCart,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Activity,
  Zap,
  Loader2,
  RefreshCw,
  BarChart3,
  PieChart,
  Store,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useNavigate } from 'react-router-dom';
import { reportsService, DashboardData, StoreOverviewData } from '@/services/reports.service';
import type { CostReportItem } from '@/services/reports.service';
import { useNetworkStore } from '@/stores/network-store';
import { ProfitChart } from '@/components/ProfitChart';
import { ReceiptText } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell,
} from 'recharts';

const methodLabels: Record<string, string> = {
  CASH: 'EFECTIVO',
  CARD: 'TARJETA',
  TRANSFER: 'TRANSFERENCIA',
  CREDIT: 'CRÉDITO',
  MIXED: 'MIXTO',
};

const methodBadge: Record<string, string> = {
  EFECTIVO: 'bg-emerald-50 text-emerald-700',
  TARJETA: 'bg-blue-50 text-blue-700',
  TRANSFERENCIA: 'bg-violet-50 text-violet-700',
  CRÉDITO: 'bg-orange-50 text-orange-700',
  MIXTO: 'bg-gray-100 text-gray-600',
};

const kpiConfig = [
  {
    key: 'todaySales' as const,
    title: 'Ventas del Día',
    icon: DollarSign,
    gradient: 'from-blue-500 to-blue-600',
    format: 'currency',
  },
  {
    key: 'todayProfit' as const,
    title: 'Utilidad Neta',
    icon: TrendingUp,
    gradient: 'from-emerald-500 to-emerald-600',
    format: 'currency',
  },
  {
    key: 'productsSold' as const,
    title: 'Productos Vendidos',
    icon: Package,
    gradient: 'from-orange-500 to-orange-600',
    format: 'number',
  },
  {
    key: 'totalCustomers' as const,
    title: 'Clientes',
    icon: Users,
    gradient: 'from-violet-500 to-violet-600',
    format: 'number',
  },
  {
    key: 'criticalStock' as const,
    title: 'Stock Crítico',
    icon: AlertTriangle,
    gradient: 'from-red-500 to-red-600',
    format: 'number',
  },
  {
    key: 'pendingOrders' as const,
    title: 'Órdenes Pend.',
    icon: ShoppingCart,
    gradient: 'from-pink-500 to-pink-600',
    format: 'number',
  },
  {
    key: 'todayReturns' as const,
    title: 'Devoluciones',
    icon: ReceiptText,
    gradient: 'from-teal-500 to-teal-600',
    format: 'returns',
  },
];

function formatCurrency(n: number) {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(n: number) {
  return n.toLocaleString('es-MX');
}

function formatChange(change: number) {
  if (change === 0) return '0%';
  return (change > 0 ? '+' : '') + change.toFixed(1) + '%';
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [costsData, setCostsData] = useState<CostReportItem[]>([]);
  const mode = useNetworkStore((state) => state.mode);
  const [ownerData, setOwnerData] = useState<StoreOverviewData | null>(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const now = new Date();

  const greeting = () => {
    const h = now.getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  };

  useEffect(() => {
    const roleName = user?.role?.toLowerCase() || '';
    if (roleName.includes('vendedor') || roleName.includes('cajero')) {
      navigate('/pos', { replace: true });
    }
  }, [user, navigate]);

  const loadDashboard = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await reportsService.getDashboard(user.company.id);
      setData(result);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al cargar dashboard';
      setError(msg);
    }

    // Non-critical: load today's costs for the profit chart
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const costsResult = await reportsService.getCosts({
        companyId: user.company.id,
        startDate: dateStr,
        endDate: dateStr,
        groupBy: 'product',
      });
      setCostsData(costsResult.data || []);
    } catch {
      // Non-critical — chart will show empty state
    } finally {
      setLoading(false);
    }
  }, [user?.company?.id]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Vista del dueño (solo contra la Nube): resumen consolidado + por tienda.
  // En la caja local (Electron) este endpoint no existe y el dashboard es el de esa sucursal.
  const loadOwnerOverview = useCallback(async () => {
    if (!user?.company?.id || mode !== 'cloud') return;
    setOwnerLoading(true);
    try {
      const result = await reportsService.getOwnerOverview(user.company.id);
      setOwnerData(result);
    } catch {
      setOwnerData(null);
    } finally {
      setOwnerLoading(false);
    }
  }, [user?.company?.id, mode]);

  useEffect(() => {
    loadOwnerOverview();
  }, [loadOwnerOverview]);

  const paymentColors: Record<string, string> = {
    CASH: '#34C759',
    CARD: '#007AFF',
    TRANSFER: '#AF52DE',
    CREDIT: '#FF9500',
    MIXED: '#8E8E93',
  };

  const paymentLabels: Record<string, string> = {
    CASH: 'Efectivo',
    CARD: 'Tarjeta',
    TRANSFER: 'Transferencia',
    CREDIT: 'Crédito',
    MIXED: 'Mixto',
  };

  const paymentChartData = useMemo(() =>
    Object.entries(data?.paymentBreakdown || {}).map(([method, amount]) => ({
      name: paymentLabels[method] || method,
      value: amount as number,
      color: paymentColors[method] || '#8E8E93',
    })).sort((a, b) => b.value - a.value),
    [data?.paymentBreakdown]
  );

  const trendChartData = useMemo(() =>
    (data?.salesTrend || []).map(d => ({
      date: new Date(d.date + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' }),
      total: d.total,
      count: d.count,
    })),
    [data?.salesTrend]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-blue-500" />
          <p className="text-sm text-gray-400">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle size={40} className="text-red-400" />
        <p className="text-sm text-red-500 font-medium">{error}</p>
        <button onClick={loadDashboard} className="flex items-center gap-2 text-sm bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
          <RefreshCw size={16} />
          Reintentar
        </button>
      </div>
    );
  }

  const kpis = data?.kpis;
  const recentSales = data?.recentSales || [];
  const criticalStock = data?.criticalStock || [];
  const topProducts = data?.topProducts || [];
  const renderCreditNotesSection = () => {
    const notes = (data?.recentCreditNotes || []) as Array<any>;
    if (notes.length === 0) return null;
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-teal-50 flex items-center justify-center">
              <ReceiptText size={13} className="text-teal-600" />
            </div>
            <span className="text-sm font-semibold text-gray-800">
              Notas de Crédito Recientes
              <span className="text-xs text-gray-400 font-normal ml-2">({data?.todayReturns?.issued || 0} hoy)</span>
            </span>
          </div>
          <button
            onClick={() => navigate('/credit-notes')}
            className="text-xs text-teal-500 hover:text-teal-600 font-medium flex items-center gap-0.5 transition-colors"
          >
            Ver todas <ChevronRight size={12} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="text-left text-[11px] text-gray-400 font-semibold px-4 py-2.5">Folio</th>
                <th className="text-left text-[11px] text-gray-400 font-semibold px-4 py-2.5">Venta Origen</th>
                <th className="text-right text-[11px] text-gray-400 font-semibold px-4 py-2.5">Total</th>
                <th className="text-right text-[11px] text-gray-400 font-semibold px-4 py-2.5">Saldo</th>
                <th className="text-center text-[11px] text-gray-400 font-semibold px-4 py-2.5">Estado</th>
                <th className="text-left text-[11px] text-gray-400 font-semibold px-4 py-2.5">Creado por</th>
                <th className="text-left text-[11px] text-gray-400 font-semibold px-4 py-2.5">Motivo</th>
                <th className="text-right text-[11px] text-gray-400 font-semibold px-4 py-2.5">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {notes.map((note) => {
                const statusStyles: Record<string, string> = { ACTIVE: 'bg-emerald-50 text-emerald-700', PARTIALLY_USED: 'bg-amber-50 text-amber-700', FULLY_USED: 'bg-gray-100 text-gray-500', EXPIRED: 'bg-red-50 text-red-600' };
                const statusLabels: Record<string, string> = { ACTIVE: 'Activa', PARTIALLY_USED: 'Parcial', FULLY_USED: 'Usada', EXPIRED: 'Expirada' };
                const usagePct = note.total > 0 ? Math.round(((note.total - note.remaining) / note.total) * 100) : 0;
                const d = new Date(note.createdAt);
                const dateStr = d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit' });
                const timeStr = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                return (
                  <tr key={note.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-2.5"><code className="text-xs font-mono text-gray-700 font-medium">{note.folio}</code></td>
                    <td className="px-4 py-2.5"><code className="text-[11px] font-mono text-gray-400">{note.originalSaleFolio}</code></td>
                    <td className="px-4 py-2.5 text-right"><span className="text-xs font-semibold text-gray-800">{formatCurrency(note.total)}</span></td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs font-semibold text-gray-800">{formatCurrency(note.remaining)}</span>
                        <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div className={['h-full rounded-full transition-all', usagePct >= 100 ? 'bg-gray-400' : usagePct > 0 ? 'bg-amber-400' : 'bg-emerald-400'].join(' ')} style={{ width: Math.min(100, usagePct) + '%' }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', statusStyles[note.status] || 'bg-gray-100 text-gray-600')}>{statusLabels[note.status] || note.status}</span>
                    </td>
                    <td className="px-4 py-2.5"><span className="text-xs text-gray-600">{note.createdBy}</span></td>
                    <td className="px-4 py-2.5 max-w-[180px]"><p className="text-xs text-gray-500 truncate" title={note.reason}>{note.reason || '—'}</p></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap"><span className="text-[11px] text-gray-400">{dateStr} {timeStr}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">

      {/* ── Hero Header ── */}
      <div
        className="rounded-xl p-5 flex items-center justify-between overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, #1a1f2e 0%, #1e3a5f 60%, #1a4080 100%)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #60a5fa 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 left-1/3 w-24 h-24 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/50 text-xs font-medium">Sistema activo</span>
          </div>
          <h1 className="text-xl font-bold text-white">
            {greeting()}, {user?.fullName?.split(' ')[0] || 'Usuario'} 👋
          </h1>
          <p className="text-white/50 text-xs mt-0.5">
            {now.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="relative z-10 hidden sm:flex flex-col items-end gap-2">
          <button
            onClick={() => navigate('/pos')}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all active:scale-95"
          >
            <Zap size={13} />
            Ir al POS
          </button>
          <button
            onClick={() => navigate('/reports')}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white/80 text-xs font-medium px-4 py-2 rounded-lg transition-all"
          >
            <Activity size={13} />
            Ver Reportes
          </button>
        </div>
      </div>

      {/* ── KPI Grid ── */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {kpiConfig.map((cfg) => {
            const kpi = kpis[cfg.key];
            let value: string;
            if (cfg.format === 'returns') {
              value = formatNumber(kpi.value);
            } else if (cfg.format === 'currency') {
              value = formatCurrency(kpi.value);
            } else {
              value = formatNumber(kpi.value);
            }
            return (
              <div
                key={cfg.key}
                className="bg-white rounded-xl p-3.5 border border-gray-100 hover:shadow-md transition-all duration-200 cursor-default group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br', cfg.gradient)}>
                    <cfg.icon size={15} className="text-white" />
                  </div>
                  <span className={cn(
                    'flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5',
                    kpi.trend === 'up' ? 'text-emerald-600 bg-emerald-50' : 'text-red-500 bg-red-50'
                  )}>
                    {kpi.detail ? (
                      kpi.detail
                    ) : (
                      <>
                        {kpi.trend === 'up' ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {kpi.change !== 0 ? formatChange(kpi.change) : '—'}
                      </>
                    )}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-900 tracking-tight">{value}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 font-medium">{cfg.title}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Vista del Dueño: Resumen por Tienda (solo en la Nube) ── */}
      {ownerLoading && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center justify-center gap-2 text-xs text-gray-400">
          <Loader2 size={14} className="animate-spin" /> Cargando resumen por tienda...
        </div>
      )}
      {!ownerLoading && ownerData && ownerData.branches.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center">
                <Store size={13} className="text-violet-600" />
              </div>
              <span className="text-sm font-semibold text-gray-800">Resumen por Tienda</span>
              <span className="text-[10px] bg-violet-50 text-violet-500 px-1.5 py-0.5 rounded-full font-semibold">DUEÑO</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              Actualizado {ownerData.asOf ? new Date(ownerData.asOf).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
          </div>

          <div className="p-4">
            {/* Total consolidado de todas las tiendas */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Ventas hoy · Todas</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(ownerData.totals.todaySales)}</p>
                <p className="text-[10px] text-gray-400">{ownerData.totals.todaySalesCount} ventas</p>
              </div>
              <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-3">
                <p className="text-[10px] text-emerald-600/70 font-medium uppercase tracking-wide">Utilidad hoy · Todas</p>
                <p className="text-lg font-bold text-emerald-700 mt-0.5">{formatCurrency(ownerData.totals.todayProfit)}</p>
                <p className="text-[10px] text-emerald-600/60">Ventas − costo</p>
              </div>
              <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Ventas del mes · Todas</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(ownerData.totals.monthSales)}</p>
                <p className="text-[10px] text-gray-400">{ownerData.totals.monthSalesCount} ventas</p>
              </div>
              <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Tiendas en línea</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{ownerData.branches.length}</p>
                <p className="text-[10px] text-gray-400">Sucursales activas</p>
              </div>
            </div>

            {/* Tabla por tienda */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-[11px] text-gray-400 font-semibold px-2 py-2">Tienda</th>
                    <th className="text-right text-[11px] text-gray-400 font-semibold px-2 py-2">Ventas Hoy</th>
                    <th className="text-right text-[11px] text-gray-400 font-semibold px-2 py-2">Utilidad Hoy</th>
                    <th className="text-right text-[11px] text-gray-400 font-semibold px-2 py-2">Ventas del Mes</th>
                    <th className="text-right text-[11px] text-gray-400 font-semibold px-2 py-2">Última Venta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {ownerData.branches.map((store) => {
                    const last = store.lastSaleAt ? new Date(store.lastSaleAt) : null;
                    const isStale = last && (Date.now() - last.getTime()) > 26 * 60 * 60 * 1000;
                    return (
                      <tr key={store.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center">
                              <Store size={11} className="text-violet-500" />
                            </div>
                            <span className="text-xs font-medium text-gray-800">{store.name}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <span className="text-xs font-semibold text-gray-800">{formatCurrency(store.todaySales)}</span>
                          <p className="text-[10px] text-gray-400">{store.todaySalesCount} ventas</p>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <span className={`text-xs font-semibold ${store.todayProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {formatCurrency(store.todayProfit)}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <span className="text-xs font-semibold text-gray-800">{formatCurrency(store.monthSales)}</span>
                        </td>
                        <td className="px-2 py-2.5 text-right whitespace-nowrap">
                          {last ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${isStale ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                              <span className="text-[11px] text-gray-500">
                                {last.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit' })} {last.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-300">Sin ventas aún</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-300 mt-3 px-1">
              Cada tienda sube sus ventas a la Nube cuando tiene internet. Si una tienda muestra una "última venta" de hace más de un día,
              probablemente su internet estuvo caído o aún no sincroniza; los datos llegan solos cuando vuelve la conexión.
            </p>
          </div>
        </div>
      )}

      {/* ── Dos columnas ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Ventas recientes (col 3) */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center">
                <ShoppingCart size={13} className="text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-gray-800">
                Ventas Recientes
                {data && <span className="text-xs text-gray-400 font-normal ml-2">({data.todaySalesCount} hoy)</span>}
              </span>
            </div>
            <button
              onClick={() => navigate('/sales')}
              className="text-xs text-blue-500 hover:text-blue-600 font-medium flex items-center gap-0.5 transition-colors"
            >
              Ver todas <ChevronRight size={12} />
            </button>
          </div>

          {recentSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-300">
              <ShoppingCart size={40} className="mb-2 opacity-50" />
              <p className="text-sm font-medium">No hay ventas hoy</p>
              <p className="text-xs mt-1">Las ventas aparecerán aquí conforme se realicen</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentSales.map((sale) => {
                const customerName = sale.customer?.trim() || 'Mostrador';
                const methodLabel = methodLabels[sale.method] || sale.method;
                return (
                  <div key={sale.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/60 transition-colors">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-gray-500">
                        {customerName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800 truncate">{customerName}</p>
                        <code className="text-[10px] text-gray-400 font-mono">{sale.folio}</code>
                        {sale.store && (
                          <span className="text-[9px] bg-violet-50 text-violet-500 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 flex-shrink-0">
                            <Store size={8} /> {sale.store}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400">{sale.items} productos · {sale.time}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-bold text-gray-900">{formatCurrency(sale.total)}</span>
                      <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', methodBadge[methodLabel] || 'bg-gray-100 text-gray-600')}>
                        {methodLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stock crítico (col 2) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center">
                <AlertTriangle size={13} className="text-red-500" />
              </div>
              <span className="text-sm font-semibold text-gray-800">Stock Crítico</span>
            </div>
            <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
              {criticalStock.length} alertas
            </span>
          </div>

          {criticalStock.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-300">
              <Package size={40} className="mb-2 opacity-50" />
              <p className="text-sm font-medium">Sin productos críticos</p>
              <p className="text-xs mt-1">Todos los productos tienen stock suficiente</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {criticalStock.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/60 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{p.category} · mín {p.minStock} uds</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={cn(
                      'text-xs font-bold',
                      p.stock <= 2 ? 'text-red-600' : 'text-orange-500'
                    )}>
                      {p.stock} uds
                    </span>
                    <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={cn('h-full rounded-full transition-all', p.stock <= 2 ? 'bg-red-400' : 'bg-orange-400')}
                        style={{ width: `${Math.min(100, (p.stock / p.minStock) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="px-4 py-3 border-t border-gray-50">
            <button
              onClick={() => navigate('/products')}
              className="w-full text-xs text-center text-red-500 hover:text-red-600 font-medium transition-colors"
            >
              Gestionar inventario →
            </button>
          </div>
        </div>

      </div>

      {/* ── Notas de Crédito Recientes ── */}
{renderCreditNotesSection()}

      {/* ── Fila de 3 gráficas ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Tendencia de Ventas (7 días) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
              <TrendingUp size={14} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Tendencia de Ventas — Últimos 7 Días</h2>
              <p className="text-[11px] text-gray-400">Evolución diaria de ingresos</p>
            </div>
          </div>
          <div className="h-[220px]">
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendChartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#007AFF" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs">
                          <p className="font-semibold text-gray-800 mb-1">{d.date}</p>
                          <p className="text-blue-600">${d.total.toFixed(2)}</p>
                          <p className="text-gray-400 mt-0.5">{d.count} ventas</p>
                        </div>
                      );
                    }}
                    cursor={{ fill: 'rgba(0,122,255,0.03)' }}
                  />
                  <Area type="monotone" dataKey="total" stroke="#007AFF" strokeWidth={2} fill="url(#salesGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-300">
                <TrendingUp size={32} className="opacity-50" />
              </div>
            )}
          </div>
        </div>

        {/* Métodos de Pago */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-md bg-green-50 flex items-center justify-center">
              <PieChart size={14} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Métodos de Pago — Hoy</h2>
              <p className="text-[11px] text-gray-400">Distribución de ingresos</p>
            </div>
          </div>
          <div className="h-[220px]">
            {paymentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={paymentChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {paymentChartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const total = paymentChartData.reduce((s, i) => s + i.value, 0);
                      const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
                      return (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs">
                          <p className="font-semibold text-gray-800 mb-1">{d.name}</p>
                          <p className="font-bold">${d.value.toFixed(2)}</p>
                          <p className="text-gray-400">{pct}%</p>
                        </div>
                      );
                    }}
                  />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-300">
                <PieChart size={32} className="opacity-50" />
              </div>
            )}
          </div>
          {/* Leyenda */}
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {paymentChartData.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="text-[10px] text-gray-500">{p.name}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Fila de Top Productos + ProfitChart ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Top Productos */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center">
              <Package size={14} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Top Productos — Hoy</h2>
              <p className="text-[11px] text-gray-400">Los más vendidos del día</p>
            </div>
          </div>
          {topProducts.length > 0 ? (
            <div className="space-y-2">
              {topProducts.slice(0, 5).map((p, i) => (
                <div key={p.sku} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-700' : 'bg-gray-200 text-gray-500'}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-[10px] text-gray-400">{p.quantity} vendidos</p>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(p.revenue)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-gray-300">
              <Package size={28} className="mb-1 opacity-50" />
              <p className="text-xs font-medium">Sin ventas hoy</p>
            </div>
          )}
        </div>

        {/* ProfitChart */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
              <BarChart3 size={14} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Utilidad por Producto — Hoy</h2>
              <p className="text-[11px] text-gray-400">Top productos con mayor contribución</p>
            </div>
          </div>
          <ProfitChart data={costsData} groupBy="product" />
        </div>

      </div>
    </div>
  );
}
